import { z } from "zod";
import { day } from "./appointments";
import { recordId } from "./contracts";

export const DOCUMENT_FILENAME_MAX_LENGTH = 255;
export const DOCUMENT_PATH_MAX_LENGTH = 1024;
export const DOCUMENT_LINK_SECONDS = 300;

export const documentContentType = z.enum(["application/pdf", "image/png", "image/jpeg", "image/webp", "image/gif", "image/heic", "image/heif"]);
export type DocumentContentType = z.infer<typeof documentContentType>;

const EXTENSIONS_OF_DOCUMENT_TYPE: Record<DocumentContentType, readonly [string, ...string[]]> = {
  "application/pdf": ["pdf"],
  "image/png": ["png"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/webp": ["webp"],
  "image/gif": ["gif"],
  "image/heic": ["heic"],
  "image/heif": ["heif"],
};

export const DOCUMENT_FILE_EXTENSIONS: readonly string[] = Object.values(EXTENSIONS_OF_DOCUMENT_TYPE).flat();

export const documentContentTypeOfExtension = (extension: string): DocumentContentType | undefined =>
  documentContentType.options.find((contentType) => EXTENSIONS_OF_DOCUMENT_TYPE[contentType].includes(extension.toLowerCase()));

export const documentFileExtension = z
  .string()
  .trim()
  .toLowerCase()
  .refine((extension) => DOCUMENT_FILE_EXTENSIONS.includes(extension), `A document is one of ${DOCUMENT_FILE_EXTENSIONS.join(", ")}.`);

const ownerId = recordId.toLowerCase();

export const workerContractDocuments = z.object({ kind: z.literal("workerContract"), workerContract: ownerId });

export const subcontractDocuments = z.object({ kind: z.literal("subcontract"), subcontract: ownerId });

export const vendorDocuments = z.object({ kind: z.literal("vendor"), customer: ownerId, vendor: ownerId });

export const memberDocuments = z.object({ kind: z.literal("member"), organization: ownerId, person: ownerId });

export const documentOwner = z.discriminatedUnion("kind", [workerContractDocuments, subcontractDocuments, vendorDocuments, memberDocuments]);
export type DocumentOwner = z.infer<typeof documentOwner>;
export type DocumentOwnerKind = DocumentOwner["kind"];

export const documentListQuery = z.union([
  z.object({ workerContract: ownerId }).strict().transform(({ workerContract }): DocumentOwner => ({ kind: "workerContract", workerContract })),
  z.object({ subcontract: ownerId }).strict().transform(({ subcontract }): DocumentOwner => ({ kind: "subcontract", subcontract })),
  z.object({ customer: ownerId, vendor: ownerId }).strict().transform(({ customer, vendor }): DocumentOwner => ({ kind: "vendor", customer, vendor })),
  z.object({ organization: ownerId, person: ownerId }).strict().transform(({ organization, person }): DocumentOwner => ({ kind: "member", organization, person })),
]);

export function documentListQueryOf(owner: DocumentOwner): Record<string, string> {
  switch (owner.kind) {
    case "workerContract":
      return { workerContract: owner.workerContract };
    case "subcontract":
      return { subcontract: owner.subcontract };
    case "vendor":
      return { customer: owner.customer, vendor: owner.vendor };
    case "member":
      return { organization: owner.organization, person: owner.person };
  }
}

export const documentRecord = z.object({
  id: recordId,
  owner: documentOwner,
  filename: z.string(),
  uploadedAt: z.string(),
  uploadedBy: recordId,
  expiresOn: day.nullable(),
});
export type DocumentRecord = z.infer<typeof documentRecord>;

export const documentList = z.object({ items: z.array(documentRecord) });
export type DocumentList = z.infer<typeof documentList>;

export const documentUploadRequest = z.object({ owner: documentOwner, extension: documentFileExtension }).strict();
export type DocumentUploadRequest = { owner: DocumentOwner; extension: string };

export const documentUpload = z.object({ path: z.string(), uploadUrl: z.string().url() });
export type DocumentUpload = z.infer<typeof documentUpload>;

const filename = z
  .string()
  .trim()
  .min(1)
  .max(DOCUMENT_FILENAME_MAX_LENGTH)
  .refine((value) => !value.includes("\0"), "no NUL characters");

export const documentRecordRequest = z
  .object({
    owner: documentOwner,
    path: z.string().min(1).max(DOCUMENT_PATH_MAX_LENGTH),
    filename,
    expiresOn: day.nullable().default(null),
  })
  .strict();
export type DocumentRecordRequest = { owner: DocumentOwner; path: string; filename: string; expiresOn?: string | null };

export const documentExpiryRequest = z.object({ expiresOn: day.nullable() }).strict();
export type DocumentExpiryRequest = z.infer<typeof documentExpiryRequest>;

export const documentLink = z.object({ url: z.string().url(), expiresAt: z.string() });
export type DocumentLink = z.infer<typeof documentLink>;
